import { PROJECT_SIDEBAR_GROUPS, type ProjectSidebarItem } from '../data/projectSidebarConfig';

/** Folge-Abruf je Element einer bereits gesammelten Liste. */
export interface EachSpec {
  /** Feld des Listenelements, das die ID trägt. */
  idField: string;
  child: (projectId: string, itemId: string) => string;
}

export interface EndpointSpec {
  path: (projectId: string) => string;
  query?: Record<string, string>;
  /** Der Pfad wird abgerufen; danach je Listenelement der Folge-Abruf. */
  each?: EachSpec[];
}

export interface ResolvedEndpoint {
  path: string;
  query?: Record<string, string>;
  each?: Array<{ idField: string; child: (itemId: string) => string }>;
}

const P = (rest = '') => (id: string) => `/api/v1/projects/${id}${rest}`;
const ep = (rest: string, extra?: Partial<EndpointSpec>): EndpointSpec => ({ path: P(rest), ...extra });

const BOARDS: EndpointSpec = ep('/kanban/boards', { query: { scope: 'project', active: '1' } });
const EMPLOYEE_PER_PARTICIPANT: EachSpec = { idField: 'coworker_id', child: (_p, cid) => `/api/v1/employees/${cid}` };
const TASK_PARTICIPANTS: EachSpec = { idField: 'id', child: (p, tid) => `/api/v1/projects/${p}/tasks/${tid}/participants` };
const TASK_SKILLS: EachSpec = { idField: 'id', child: (p, tid) => `/api/v1/projects/${p}/tasks/${tid}/skills` };
const AP_BOARDS: EachSpec = { idField: 'id', child: (p, ap) => `/api/v1/projects/${p}/arbeitspakete/${ap}/kanban/boards` };
const SNAPSHOT: EachSpec = { idField: 'id', child: (p, sid) => `/api/v1/projects/${p}/retro-snapshots/${sid}` };

/**
 * Immer enthalten: Projekt, Teilnehmer (+ je Teilnehmer der Mitarbeiter, nicht die
 * Mandanten-Personenliste), Dashboard und Arbeitspakete — Shell/Sidebar braucht
 * Dashboard für die Reifegrad-Ampeln und Arbeitspakete für die AP-Unterpunkte.
 */
export const BASE_ENDPOINTS: EndpointSpec[] = [
  ep(''),
  ep('/participants', { each: [EMPLOYEE_PER_PARTICIPANT] }),
  ep('/dashboard'),
  ep('/arbeitspakete'),
];

const TASKS_AND_DETAILS = ep('/tasks', { each: [TASK_PARTICIPANTS, TASK_SKILLS] });

/** Schlüssel = Seitenschlüssel aus PROJECT_SIDEBAR_GROUPS. Leer = nur Basis. */
export const PAGE_ENDPOINTS: Record<string, EndpointSpec[]> = {
  dashboard: [
    ep('/milestones'), ep('/weekly-progress'), ep('/meetings'), ep('/offene-punkte'),
    ep('/initialization'), ep('/time-summary'), ep('/billing/summary'),
  ],
  prozesslandkarte: [],
  stakeholders: [ep('/stakeholders'), ep('/stakeholder-matrix')],
  machbarkeit: [ep('/initialization'), ep('/uncertainties'), ep('/milestones')],
  projektauftrag: [ep('/config'), ep('/initialization'), ep('/team'), ep('/uncertainties')],
  ziele: [ep('/initialization'), ep('/uncertainties'), ep('/milestones')],
  team: [ep('/team')],
  zeiten: [ep('/initialization'), ep('/uncertainties'), ep('/milestones')],
  projektplan: [
    TASKS_AND_DETAILS, ep('/arbeitspakete'), ep('/meetings'), ep('/offene-punkte'),
    ep('/milestones'), ep('/initialization'), ep('/gantt-data'),
    { path: () => '/api/v1/skill-names' },
  ],
  anforderungen: [ep('/anforderungen'), { path: () => '/api/v1/anforderung-kategorien' }],
  scope: [ep('/config'), ep('/attachments')],
  arbeitspakete: [
    ep('/arbeitspakete', { each: [AP_BOARDS] }), ep('/arbeitspakete/dependencies'), ep('/wbs-dictionary'),
    ep('/milestones'), ep('/team'), ep('/meetings'), ep('/offene-punkte'), TASKS_AND_DETAILS,
  ],
  risiken: [ep('/risiken')],
  meetings: [ep('/meetings')],
  'offene-punkte': [ep('/offene-punkte'), ep('/anforderungen'), ep('/arbeitspakete'), ep('/meetings')],
  kanban: [BOARDS, ep('/kanban'), { path: () => '/api/v1/kanban-settings' }, ep('/arbeitspakete'), ep('/tasks')],
  burnup: [BOARDS, ep('/tasks'), ep('/weekly-progress/burnup')],
  status: [ep('/weekly-progress'), ep('/dashboard'), ep('/tasks'), ep('/risiken'), ep('/offene-punkte')],
  retro: [ep('/retro-items'), ep('/retro-snapshots', { each: [SNAPSHOT] })],
  abschluss: [ep('/abschluss')],
};

/** Löst Seitenschlüssel zu Abrufen auf; dedupliziert nach Pfad+Query, führt each-Abrufe zusammen. */
export function endpointsFor(pages: string[], projectId: string): ResolvedEndpoint[] {
  const out = new Map<string, ResolvedEndpoint>();
  const eachKeys = new Map<string, Set<string>>();
  const specs = [...BASE_ENDPOINTS, ...pages.flatMap((p) => PAGE_ENDPOINTS[p] ?? [])];
  for (const spec of specs) {
    const path = spec.path(projectId);
    const key = path + JSON.stringify(spec.query ?? {});
    let entry = out.get(key);
    if (!entry) {
      entry = { path, ...(spec.query ? { query: spec.query } : {}) };
      out.set(key, entry);
      eachKeys.set(key, new Set());
    }
    for (const e of spec.each ?? []) {
      const sig = e.child(projectId, '{id}') + '|' + e.idField;
      const seen = eachKeys.get(key)!;
      if (seen.has(sig)) continue;
      seen.add(sig);
      (entry.each ??= []).push({ idField: e.idField, child: (id) => e.child(projectId, id) });
    }
  }
  return [...out.values()];
}

export interface ExportablePage {
  key: string;
  labelKey: string;
  children?: ExportablePage[];
}
export interface ExportableGroup {
  key: string;
  labelKey: string;
  items: ExportablePage[];
}

const toPage = (i: ProjectSidebarItem): ExportablePage => ({
  key: i.key,
  labelKey: i.labelKey,
  ...(i.children?.length ? { children: i.children.map(toPage) } : {}),
});

/** Gruppen und Seiten (inkl. Kinder) für den Dialog. Labels: t(labelKey) im Namespace 'projects'. */
export function listExportablePages(): ExportableGroup[] {
  return PROJECT_SIDEBAR_GROUPS.map((g) => ({ key: g.key, labelKey: g.labelKey, items: g.items.map(toPage) }));
}
